import { AiSettingsVersionFragment } from 'lib-knowledge-management-common/graphql';
import { useModal } from 'lib-knowledge-modal';
import { useNotification } from 'lib-knowledge-utils/hooks/useNotification';
import { routeGenerator } from 'lib-knowledge-utils/routes';
import { useNavigate } from 'react-router-dom';

import { ReplaceDraftModal } from '../modals/replaceDraftModal';
import { toDraftVariables, toFormValues } from '../utils/aiSettingsForm';

import { useUpdateAiSettingsDraft } from './useAiSettingsMutations';

/**
 * Makes a saved version the working draft. The backend has no restore, so its
 * settings are written over the draft, which is replaced whole anyway.
 *
 * Asks first only when the draft holds changes no version has; otherwise the
 * draft is just the latest version, and nothing is lost.
 */
export function useEditVersion({
  version,
  hasDraft,
}: {
  version: AiSettingsVersionFragment | undefined;
  hasDraft: boolean;
}): () => void {
  const { showModal } = useModal();
  const { successNotify, errorNotify } = useNotification();
  const navigate = useNavigate();
  const [updateDraft] = useUpdateAiSettingsDraft();

  const restore = async () => {
    if (!version) return;
    try {
      await updateDraft({
        variables: toDraftVariables(toFormValues(version)),
      });
      successNotify({
        title: 'Draft edited',
        text: 'This version is now your draft.',
      });
      navigate(routeGenerator.aiSuggestionsSettings());
    } catch {
      errorNotify({ text: 'The draft could not be edited. Try again.' });
    }
  };

  return () => {
    if (hasDraft) {
      showModal(ReplaceDraftModal, { onDiscard: restore });
    } else {
      void restore();
    }
  };
}
